import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Search, 
  Download, 
  Filter, 
  MoreVertical, 
  Mail, 
  Phone, 
  Calendar,
  ExternalLink,
  CheckCircle2,
  Trash2,
  Target,
  UserPlus,
  RefreshCcw,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/authStore';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface Lead {
  id: string;
  email: string;
  name: string;
  phone: string;
  message: string;
  created_at: string;
  is_read: boolean;
  crm_sync_status: 'pending' | 'success' | 'failed' | 'skipped';
  crm_sync_error: string | null;
  crm_lead_ids: Record<string, string>;
  qr_codes: {
    id: string;
    title: string;
  };
}

export default function Leads() {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [isBulkSyncing, setIsBulkSyncing] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [user]);

  const fetchLeads = async () => {
    if (!user) return;
    setIsLoading(true);
    
    const { data, error } = await (supabase as any)
      .from('leads')
      .select(`
        *,
        qr_codes (
          id,
          title
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching leads:', error);
    } else {
      setLeads(data as any);
    }
    setIsLoading(false);
  };

  const markAsRead = async (id: string) => {
    const { error } = await (supabase as any)
      .from('leads')
      .update({ is_read: true })
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update lead status",
        variant: "destructive",
      });
    } else {
      setLeads(leads.map(l => l.id === id ? { ...l, is_read: true } : l));
    }
  };

  const deleteLead = async (id: string) => {
    const { error } = await (supabase as any)
      .from('leads')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete lead",
        variant: "destructive",
      });
    } else {
      setLeads(leads.filter(l => l.id !== id));
      toast({
        title: "Deleted",
        description: "Lead has been removed",
      });
    }
  };

  const exportLeads = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'QR Code', 'Message'];
    const csvContent = [
      headers.join(','),
      ...leads.map(l => [
        format(new Date(l.created_at), 'yyyy-MM-dd HH:mm'),
        `"${l.name || ''}"`,
        l.email || '',
        l.phone || '',
        `"${l.qr_codes?.title || ''}"`,
        `"${(l.message || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `nexqr-leads-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const retrySync = async (leadId: string) => {
    toast({
      title: "Syncing...",
      description: "Retrying CRM synchronization",
    });
    
    // We'll call the hubspot-sync function manually here or let the user choose
    // For simplicity, we trigger the common sync flow
    const { data, error } = await supabase.functions.invoke('hubspot-sync', {
      body: { lead_id: leadId }
    });

    if (error) {
      toast({
        title: "Sync Failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Sync Success",
        description: "Lead synchronized with HubSpot",
      });
      fetchLeads(); // Refresh list
    }
  };

  const toggleLeadSelection = (id: string) => {
    setSelectedLeads(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const bulkSync = async () => {
    if (selectedLeads.length === 0) return;
    
    setIsBulkSyncing(true);
    toast({
      title: "Bulk Syncing...",
      description: `Syncing ${selectedLeads.length} leads to HubSpot`,
    });

    let successCount = 0;
    let failCount = 0;

    for (const leadId of selectedLeads) {
      const { error } = await supabase.functions.invoke('hubspot-sync', {
        body: { lead_id: leadId }
      });
      if (error) failCount++;
      else successCount++;
    }

    setIsBulkSyncing(false);
    setSelectedLeads([]);
    fetchLeads();

    toast({
      title: "Bulk Sync Completed",
      description: `Successfully synced ${successCount} leads. ${failCount} failed.`,
      variant: failCount > 0 ? "destructive" : "default",
    });
  };

  const filteredLeads = leads.filter(l => 
    (l.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
     l.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     l.qr_codes?.title?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const stats = {
    total: leads.length,
    new: leads.filter(l => !l.is_read).length,
    synced: leads.filter(l => l.crm_sync_status === 'success').length,
    failed: leads.filter(l => l.crm_sync_status === 'failed').length
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 flex items-center gap-2">
              <Users className="h-8 w-8 text-primary" />
              Leads
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Manage data captured from your landing pages
            </p>
          </div>
          <div className="flex gap-2">
            {selectedLeads.length > 0 && (
              <Button 
                variant="default" 
                size="sm" 
                onClick={bulkSync} 
                disabled={isBulkSyncing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isBulkSyncing ? (
                  <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4 mr-2" />
                )}
                Sync {selectedLeads.length} to CRM
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportLeads} disabled={leads.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase font-semibold">Total Leads</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-green-50 border-green-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase font-semibold">New Leads</p>
                <p className="text-xl font-bold text-green-700">{stats.new}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-blue-50 border-blue-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <RefreshCcw className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase font-semibold">CRM Synced</p>
                <p className="text-xl font-bold text-blue-700">{stats.synced}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-orange-50 border-orange-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase font-semibold">Sync Failed</p>
                <p className="text-xl font-bold text-orange-700">{stats.failed}</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="mb-6">
          <div className="p-4 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name, email, or QR title..." 
                className="pl-10"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>
        </Card>

        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Input 
                      type="checkbox" 
                      className="h-4 w-4" 
                      checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                      onChange={() => {
                        if (selectedLeads.length === filteredLeads.length) setSelectedLeads([]);
                        else setSelectedLeads(filteredLeads.map(l => l.id));
                      }}
                    />
                  </TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>CRM Sync</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(6)].map((_, j) => (
                        <TableCell key={j}><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      No leads found. Start by enabling Lead Forms on your Bio Pages.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead.id} className={lead.is_read ? 'opacity-70' : 'bg-primary/5'}>
                      <TableCell>
                        <Input 
                          type="checkbox" 
                          className="h-4 w-4" 
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => toggleLeadSelection(lead.id)}
                        />
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-medium">{format(new Date(lead.created_at), 'MMM dd, yyyy')}</span>
                          <span className="text-[10px] text-muted-foreground">{format(new Date(lead.created_at), 'HH:mm')}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-semibold text-sm">{lead.name || 'Anonymous'}</span>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" /> {lead.email}
                          </div>
                          {lead.phone && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3" /> {lead.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal text-xs whitespace-nowrap">
                          {lead.qr_codes?.title || 'Unknown QR'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {lead.crm_sync_status === 'success' ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Synced
                            </Badge>
                          ) : lead.crm_sync_status === 'failed' ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px] flex items-center gap-1 cursor-pointer" onClick={() => retrySync(lead.id)}>
                              <AlertCircle className="h-3 w-3" /> Failed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-[10px]">
                              {lead.crm_sync_status || 'Pending'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <p className="text-xs line-clamp-2 italic">{lead.message || 'No message'}</p>
                      </TableCell>
                      <TableCell>
                        {lead.is_read ? (
                          <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-[10px]">Read</Badge>
                        ) : (
                          <Badge className="bg-primary text-white text-[10px]">New</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!lead.is_read && (
                              <DropdownMenuItem onClick={() => markAsRead(lead.id)}>
                                <CheckCircle2 className="h-4 w-4 mr-2" /> Mark as Read
                              </DropdownMenuItem>
                            )}
                            {lead.crm_sync_status !== 'success' && (
                              <DropdownMenuItem onClick={() => retrySync(lead.id)}>
                                <RefreshCcw className="h-4 w-4 mr-2" /> Retry Sync
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => deleteLead(lead.id)} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" /> Delete Lead
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
